import { gql, useMutation } from "@apollo/client";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { Button, MenuItem, Select, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Auth } from "aws-amplify";
import { ErrorBoxDialog } from "components/error";
import copy from "copy-to-clipboard";
import { useSolutionKey } from "features/solution";
import { ScsUserType } from "model/schema/globalTypes";
import { useState } from "react";
import {
  CreateStreamingUrl,
  CreateStreamingUrlVariables
} from "./schema/CreateStreamingUrl";
export const mutationCreateStreamingUrl = gql`
  mutation CreateStreamingUrl(
    $solutionKey: Key!
    $projectId: ID!
    $refreshToken: String!
    $userType: ScsUserType!
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        projects {
          createStreamingUrl(
            projectId: $projectId
            refreshToken: $refreshToken
            userType: $userType
          )
        }
      }
    }
  }
`;

const useStyles = makeStyles(theme => ({
  secondaryButton: {
    marginRight: 5
  },
  select: {
    marginRight: 5,
    minWidth: 120,
    height: 36,
    backgroundColor: "#3aa9ae",
    color: theme.palette.common.white,
    boxShadow:
      "rgba(0, 0, 0, 0.2) 0px 3px 1px -2px, rgba(0, 0, 0, 0.14) 0px 2px 2px 0px, rgba(0, 0, 0, 0.12) 0px 1px 5px 0px",
    "&:hover": {
      backgroundColor: "rgb(49, 140, 169)"
    },
    "& .MuiSvgIcon-root": {
      color: theme.palette.common.white
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none"
    },
    "&.Mui-disabled": {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
      color: "rgba(0, 0, 0, 0.26)",
      "& .MuiSvgIcon-root": {
        color: "rgba(0, 0, 0, 0.26)"
      }
    }
  }
}));

export default function ProjectStreamingButtons({
  projectId,
  isAvailable
}: {
  projectId: string;
  isAvailable: boolean;
}) {
  const solutionKey = useSolutionKey();
  const classNames = useStyles();
  const [userType, setUserType] = useState<ScsUserType | "">("");

  const [
    createStreamingUrlMutation,
    { error: mutationError, reset: resetError }
  ] = useMutation<CreateStreamingUrl, CreateStreamingUrlVariables>(
    mutationCreateStreamingUrl,
    { onError: error => console.log(error) }
  );

  const createStreamingUrl = async (selectedUserType: ScsUserType) => {
    const auth = await Auth.currentSession();
    const refreshToken = auth.getRefreshToken().getToken();
    const result = await createStreamingUrlMutation({
      variables: {
        solutionKey,
        projectId,
        refreshToken,
        userType: selectedUserType
      }
    });
    return result?.data?.solutions.solution?.projects?.createStreamingUrl;
  };

  const handleStartSCS = async (selectedUserType: ScsUserType) => {
    const url = await createStreamingUrl(selectedUserType);
    if (url) window.open(url, "_blank");
  };

  return (
    <>
      <Tooltip title="Connect to this project.">
        <Select
          value={userType}
          onChange={e => {
            const selected = e.target.value as ScsUserType;
            setUserType(selected);
            handleStartSCS(selected);
          }}
          variant="outlined"
          className={classNames.select}
          disabled={!isAvailable}
          data-testid="create-streaming-url"
          displayEmpty
          renderValue={value =>
            value ? `Start SCS as ${value}` : "Select user type..."
          }
        >
          {Object.values(ScsUserType).map(type => (
            <MenuItem key={type} value={type}>
              Start SCS as {type}
            </MenuItem>
          ))}
        </Select>
      </Tooltip>
      <Tooltip title="Can be used in the AppStream Client for Windows.">
        <span>
          <Button
            variant="outlined"
            className={classNames.secondaryButton}
            disabled={!isAvailable || !userType}
            data-testid="copy-streaming-url"
            onClick={async () => {
              if (userType) {
                const url = await createStreamingUrl(userType);
                if (url) copy(url);
              }
            }}
            endIcon={<FileCopyIcon />}
          >
            Copy Streaming URL
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Can be bookmarked to connect to this project immediately.">
        <span>
          <Button
            variant="outlined"
            className={classNames.secondaryButton}
            disabled={!userType}
            data-testid="copy-connect-url"
            onClick={() => {
              if (userType) {
                const url = `${window.location.origin}${window.location.pathname}/connect/${userType}`;
                if (url) copy(url);
              }
            }}
            endIcon={<FileCopyIcon />}
          >
            Copy Connect URL
          </Button>
        </span>
      </Tooltip>

      <ErrorBoxDialog apolloError={mutationError} onClose={resetError} />
    </>
  );
}
